import React, { useCallback, useMemo } from 'react';
import { Switch } from '@heroui/react';
import { DebouncedInput } from '../@components/DebouncedInput.jsx';

function splitKeywords(value) {
  return `${value ?? ''}`
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export default function AhrefsFirehosePrimitiveConfigurator({ primitive }) {
  const streamConfig = useMemo(() => {
    const stream = primitive?.referenceParameters?.stream ?? {};
    const trigger = stream?.trigger ?? {};
    return {
      enabled: stream.enabled === true,
      url: stream.url ?? '',
      tapToken: stream.tapToken ?? '',
      tapId: stream.tapId ?? '',
      quietPeriodMs: trigger.quietPeriodMs ?? 60000,
      minNewRecords: trigger.minNewRecords ?? 1,
      keywordAny: Array.isArray(trigger.keywordAny) ? trigger.keywordAny.join(', ') : '',
    };
  }, [primitive?.referenceParameters?.stream]);

  const updateField = useCallback(async (path, value) => {
    if (!primitive?.setField) {
      return;
    }
    try {
      await primitive.setField(path, value);
    } catch (error) {
      console.error('Unable to save Ahrefs firehose configuration', error);
    }
  }, [primitive]);

  return (
    <div className="space-y-4">
      <div className="rounded-medium border border-default-200 bg-default-50 p-4">
        <p className="text-sm text-default-600">
          This external step acts as a streamed ingress point. Set the Firehose tap token here; no workspace-level
          integration account is required for streaming.
        </p>
      </div>

      <div className="flex items-center justify-between rounded-medium border border-default-200 px-4 py-3">
        <div>
          <p className="text-sm font-medium text-default-700">Enable stream</p>
          <p className="text-xs text-default-500">When enabled, the stream manager opens an SSE connection for this step instance.</p>
        </div>
        <Switch
          isSelected={streamConfig.enabled}
          onValueChange={(value) => updateField('referenceParameters.stream.enabled', value)}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-default-600">SSE URL</label>
        <DebouncedInput
          className="w-full"
          variant="bordered"
          placeholder="https://..."
          value={streamConfig.url}
          onChange={(value) => updateField('referenceParameters.stream.url', value?.trim() ? value.trim() : null)}
        />
        <p className="text-xs text-default-500">Optional. Defaults to the Firehose stream endpoint when blank.</p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-default-600">Tap token</label>
        <DebouncedInput
          className="w-full"
          variant="bordered"
          type="password"
          placeholder="fh_..."
          value={streamConfig.tapToken}
          onChange={(value) => updateField('referenceParameters.stream.tapToken', value?.trim() ? value.trim() : null)}
        />
        <p className="text-xs text-default-500">Required for streaming. Stored on this external step configuration.</p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-default-600">Tap ID</label>
        <DebouncedInput
          className="w-full"
          variant="bordered"
          placeholder="tap_..."
          value={streamConfig.tapId}
          onChange={(value) => updateField('referenceParameters.stream.tapId', value?.trim() ? value.trim() : null)}
        />
        <p className="text-xs text-default-500">Optional metadata for bookkeeping or future tap management.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-default-600">Quiet period (ms)</label>
          <DebouncedInput
            className="w-full"
            variant="bordered"
            type="number"
            min="0"
            value={streamConfig.quietPeriodMs}
            onChange={(value) => {
              const parsed = Number(value);
              updateField(
                'referenceParameters.stream.trigger.quietPeriodMs',
                Number.isFinite(parsed) ? parsed : 0,
              );
            }}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-default-600">Min new records</label>
          <DebouncedInput
            className="w-full"
            variant="bordered"
            type="number"
            min="1"
            value={streamConfig.minNewRecords}
            onChange={(value) => {
              const parsed = Number(value);
              updateField(
                'referenceParameters.stream.trigger.minNewRecords',
                Number.isFinite(parsed) && parsed > 0 ? parsed : 1,
              );
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-default-600">Keyword trigger</label>
        <DebouncedInput
          className="w-full"
          variant="bordered"
          area
          minRows={4}
          placeholder="keyword one, keyword two"
          value={streamConfig.keywordAny}
          onChange={(value) => updateField('referenceParameters.stream.trigger.keywordAny', splitKeywords(value))}
        />
        <p className="text-xs text-default-500">Comma-separated. Leave blank to trigger on count/quiet-period rules only.</p>
      </div>
    </div>
  );
}
